import * as THREE from "three";
import useMaterial from "@/hooks/useMaterial";
import { useAppStore } from "@/stores/appStore";
import { config_base } from "@/data/config_base";
import { useEffect } from "react";
import useCopyTexture from "@/hooks/useCopyTexture";

const DoorHingeGate = ({ pillarPosition }) => {
	const { gate } = useAppStore((state) => state.configurator);
	const shape = new THREE.Shape();
	const framethickness = gate.selectedDimensions.door_frame_thickness / 500 + 0.01;
	const pieceThickness = 0.001;
	const pieceLength = gate.selectedDimensions.door_frame_width / 500 + 0.01;

	shape.moveTo(0, 0);
	shape.lineTo(0, pieceLength);
	shape.lineTo(pieceThickness, pieceLength);
	shape.lineTo(pieceThickness, pieceThickness);
	shape.lineTo(framethickness - pieceThickness, pieceThickness);
	shape.lineTo(framethickness - pieceThickness, pieceLength);
	shape.lineTo(framethickness, pieceLength);
	shape.lineTo(framethickness, 0);
	shape.closePath();

	const extrudeSettings = {
		depth: 0.03,
		bevelEnabled: true,
		bevelSegments: 5,
		steps: 1,
		bevelSize: 0.0003,
		bevelThickness: 0.001,
		curveSegments: 2,
	};

	const defaultMaterial = config_base.configurator.materials.find((material) => material.id === "worn_bronze");

	const textures = useCopyTexture(defaultMaterial.textures, {
		repeat: defaultMaterial.settings.repeat,
	});

	const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

	useEffect(() => {
		return () => {
			geometry.dispose();
		};
	}, [geometry]);

	return (
		<mesh
			castShadow
			receiveShadow
			position={[0.02, 0, -(framethickness / 2)]}
			rotation={[Math.PI / 2, 0, Math.PI / 2]}
			geometry={geometry}>
			<meshStandardMaterial
				{...textures}
				roughness={defaultMaterial.settings.roughness}
				normalScale={new THREE.Vector2(pillarPosition === "right" ? -1 : 1, 1)}
			/>
		</mesh>
	);
};

const DoorHingePillar = ({ pillarPosition }) => {
	const { gate } = useAppStore((state) => state.configurator);

	const framethickness = gate.selectedDimensions.door_frame_thickness / 500;

	const defaultMaterial = config_base.configurator.materials.find((material) => material.id === "worn_bronze");

	const textures = useCopyTexture(defaultMaterial.textures, {
		repeat: defaultMaterial.settings.repeat,
	});

	const geometry = new THREE.BoxGeometry(0.001, 0.06, framethickness + 0.01);

	useEffect(() => {
		return () => {
			geometry.dispose();
		};
	}, [geometry]);

	return (
		<mesh castShadow receiveShadow position={[0.026, -0.015, 0]} rotation={[0, 0, 0]} geometry={geometry}>
			<meshStandardMaterial
				{...textures}
				roughness={defaultMaterial.settings.roughness}
				normalScale={new THREE.Vector2(pillarPosition === "right" ? 1 : -1, 1)}
			/>
		</mesh>
	);
};

const DoorCylinder = ({ pillarPosition }) => {
	const defaultMaterial = config_base.configurator.materials.find((material) => material.id === "worn_bronze");

	const textures = useCopyTexture(defaultMaterial.textures, {
		repeat: defaultMaterial.settings.repeat,
	});

	const geometry = new THREE.CylinderGeometry(0.003, 0.003, 0.032, 20);

	useEffect(() => {
		return () => {
			geometry.dispose();
		};
	}, [geometry]);

	return (
		<mesh castShadow receiveShadow position={[0.0225, -0.015, 0]} rotation={[0, 0, 0]} geometry={geometry}>
			<meshStandardMaterial
				{...textures}
				roughness={defaultMaterial.settings.roughness}
				normalScale={new THREE.Vector2(pillarPosition === "right" ? -1 : 1, 1)}
			/>
		</mesh>
	);
};

export default function DoorHinge({ pillarPosition, position, rotation }: { position: [number, number, number] }) {
	return (
		<group position={position} rotation={rotation}>
			<DoorHingeGate pillarPosition={pillarPosition} />
			<DoorCylinder pillarPosition={pillarPosition} />
			<DoorHingePillar pillarPosition={pillarPosition} />
		</group>
	);
}
